import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import type { UseFormReturn } from 'react-hook-form'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import LoyaltyTierBenefitAvailabilityMessageBanner from 'loyalty/components/LoyaltyTierBenefitAvailabilityMessageBanner'
import type { TransferOption as BaseTransferOption } from './CheckoutLoyaltyTransferOption'
import CheckoutLoyaltyTransferOption from './CheckoutLoyaltyTransferOption'
interface TransferOption extends BaseTransferOption {
  transferOptions?: {
    toHotel?: App.ExperienceTransferOption
    toAirport?: App.ExperienceTransferOption
  }
}

interface Props {
  form: UseFormReturn<any>
  transferOptions: Array<TransferOption>
  onContinue: () => void
}

function CheckoutLoyaltyTransferSelectionStep({ form, transferOptions, onContinue }: Props) {
  const intl = useIntl()
  const { watch, setValue } = form
  const selectedTransferType = watch('transferType')

  const handleTransferTypeSelect = useCallback((transferType: string) => {
    setValue('transferType', transferType)
    let selectedTransferOption: App.ExperienceTransferOption | undefined

    const option = transferOptions.find(opt => opt.id === transferType)
    if (option?.transferOptions) {
      selectedTransferOption = option.transferOptions.toHotel || option.transferOptions.toAirport
    }

    if (selectedTransferOption) {
      setValue('selectedTransferOption', selectedTransferOption)
    }
  }, [transferOptions, setValue])

  return (
    <>
      <ModalHeader title={intl.formatMessage({
        id: '08WYbzSVwz69ktHEZ_cg',
        defaultMessage: 'Free airport chauffeur service',
        description: 'Modal title for loyalty transfer selection in the loyalty section',
      })}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={20}>
            <BodyText variant="large" colour="light-foreground-alt">
              {intl.formatMessage({
                id: 'WiWT8lt3exDMwsaYBsvN',
                defaultMessage: 'Travel in comfort with a complimentary private transfer from your chosen destination. Choose an option below.',
                description: 'Description text for loyalty transfer selection modal',
              })}
            </BodyText>

            <VerticalSpacer gap={24}>
              {transferOptions.map(option => (
                <CheckoutLoyaltyTransferOption
                  key={option.id}
                  option={option}
                  name="transferType"
                  checked={selectedTransferType === option.id}
                  onChange={handleTransferTypeSelect}
                  variant={option.isPopular ? 'highlight' : 'default'}
                />
              ))}
            </VerticalSpacer>
            <LoyaltyTierBenefitAvailabilityMessageBanner benefitType="airport_transfer" />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'rKPhhi18kSt3m5JVKtk_',
            defaultMessage: 'Continue',
            description: 'Continue button in the loyalty airport transfer selection step of the checkout review flow',
          }),
          onClick: onContinue,
        }}
      />
    </>
  )
}

export default CheckoutLoyaltyTransferSelectionStep
